import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useMemo, useState } from 'react';
import {
  deferredGroupsOf,
  MERGE_WATCH_MS,
  MERGE_WATCHED_GROUPS,
  mergeProgressOf,
  type MergeProgressView,
  type MergeStartedView,
} from '@/components/agent-chat/duplicate-cards.util';
import { isRunLive } from '@/containers/agents/run-status.util';
import {
  useGetAdvancedRunQuery,
  useGetAdvancedRunRecordsQuery,
} from '@/store/features/custom-playbooks.feature';
import { useGetDuplicateGroupQuery } from '@/store/features/gtm-agents.feature';

/** How often the merge run reads again while it is live. */
const RUN_POLL_MS = 3000;

/** A group a merge left for later, read again every 30 seconds while it waits. */
function useWatchedGroup(
  agentId: string | undefined,
  groupId: string | undefined,
  pollingInterval: number
) {
  return useGetDuplicateGroupQuery(
    agentId && groupId ? { agentId, groupId } : skipToken,
    { pollingInterval, skipPollingIfUnfocused: true }
  ).currentData;
}

/**
 * How the merge a Clu card started went (mergeProgressOf): its run, read
 * every 3 seconds while live; once it ends, the groups it left for later
 * (its records), and those groups, the first five, read every 30 seconds
 * while any still waits. An interrupted merge that a recovery run later
 * verifies reads so on the card. Reads nothing unless `enabled`
 * (Agents unlocked).
 */
export function useMergeProgress(
  started: MergeStartedView | null,
  agentId: string | undefined,
  enabled: boolean
): MergeProgressView | null {
  const runId = enabled ? (started?.runId ?? undefined) : undefined;
  const [runLive, setRunLive] = useState(true);
  const { currentData: run } = useGetAdvancedRunQuery(runId ?? skipToken, {
    pollingInterval: runLive ? RUN_POLL_MS : 0,
    skipPollingIfUnfocused: true,
  });
  useEffect(() => {
    if (run) setRunLive(isRunLive(run.status));
  }, [run]);

  // Once the run ends, the groups it left for later, when it didn't settle
  // every group itself.
  const cleanup = run?.cleanup;
  const settled =
    (cleanup?.merged ?? 0) + (cleanup?.failed ?? 0) + (cleanup?.skipped ?? 0);
  const readRecords =
    Boolean(runId && run && !isRunLive(run.status)) &&
    (started?.groupCount === null ||
      settled < (started?.groupCount ?? Number.POSITIVE_INFINITY));
  const { currentData: page } = useGetAdvancedRunRecordsQuery(
    readRecords && runId ? { runId, status: 'filtered', limit: 100 } : skipToken
  );
  const deferred = useMemo(
    () => (page ? deferredGroupsOf(page.records) : undefined),
    [page]
  );

  const [watching, setWatching] = useState(true);
  const poll = watching ? MERGE_WATCH_MS : 0;
  const watched = (deferred ?? []).slice(0, MERGE_WATCHED_GROUPS);
  // A fixed number of reads, one per slot, so the hooks never change order.
  const read = [
    useWatchedGroup(agentId, watched[0]?.id, poll),
    useWatchedGroup(agentId, watched[1]?.id, poll),
    useWatchedGroup(agentId, watched[2]?.id, poll),
    useWatchedGroup(agentId, watched[3]?.id, poll),
    useWatchedGroup(agentId, watched[4]?.id, poll),
  ];
  const groups = Object.fromEntries(
    watched.map((group, index) => [group.id, read[index]])
  );

  const progress = started
    ? mergeProgressOf(started, run, readRecords ? deferred : [], groups)
    : null;
  const stillWatching = progress?.watching ?? false;
  useEffect(() => setWatching(stillWatching), [stillWatching]);
  return progress;
}
